import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import copy from 'copy-to-clipboard';
import { withStyles, Tooltip } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloudIcon from '@material-ui/icons/Cloud';
import LayersIcon from '@material-ui/icons/Layers';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import GroupIcon from '@material-ui/icons/Group';
import Button from '@material-ui/core/Button';
import HelpIcon from '@material-ui/icons/Help';

import {
  AlertDialog,
  AlertMessages,
  Footer,
} from '../../../components';
import { getI18nFormattedMessage } from '../../../utils/intl';

import {
    fetchProxyDetails,
    fetchProxyDeploymentTypes,
    fetchGatewayDetails,
    fetchGateways,
    fetchApis,
    fetchApplicationApiKeysCount,
    fetchAccountPlans,
    fetchApiPlans,
    fetchAllOrganizations,
    fetchSelectedOrganizations,
    fetchApiSyncFeatureFlag,
    fetchApiSyncCounts,
    deleteProxy,
} from '../../../actions/apiProxy';
import {
    getProxyDetails,
    getApiKeyProxyType,
    getGatewayDetails,
    getGateways,
    getApis,
    getAccountPlans,
    getApiPlansCount,
    getApplicationApiKeysCount,
    getOrganizations,
    getSelectedOrganizations,
    getDeleteProxyStatus,
    getApiSyncFeatureFlag,
    getPortalApis,
    getPortalPublishedApis,
    getGatewayPublishedApis,
} from '../../../reducers/apiProxy';
import { getConfig, getFeatureFlags } from '../../../reducers/portalConfig';

import DetailCard from './DetailCard';
import ApiCard from './ApiCard';
import styles from './styles';

const PROXY_TYPE_ITEMS = [
  { key: 'AUTOMATIC', name: 'Automatic' },
  { key: 'ON_DEMAND', name: 'On Demand' },
  { key: 'MANUAL', name: 'Scripted' },
];

const refreshTimeInterval = 5 * 1000;

export const ApiProxyDetails = (props) => {
  const {
    proxyDetails,
    apiKeyProxyType,
    gatewayDetails,
    gateways,
    apis,
    applicationApiKeysCount,
    accountPlans,
    apiPlansCount,
    portalPublishedApis,
    gatewayPublishedApis,
    portalApis,
    organizations,
    selectedOrganizations,
    deleteProxyStatus,
    config,
    featureFlags,
    useNewAutoSync,
    history,
    classes,
  } = props;

  const dispatch = useDispatch();
  const featureFlagApiPlan = JSON.parse(get(featureFlags, 'apiPlans.Value'));
  const [copied, setCopied] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');

  const proxyUuid = get(props, 'match.params.apiProxyUuid');
  useEffect(() => {
    fetchApiSyncFeatureFlag({ config, dispatch, proxyUuid });
    fetchApiSyncCounts({ config, dispatch, proxyUuid });
    fetchProxyDetails({ config, dispatch, proxyUuid });
    fetchProxyDeploymentTypes({ config, dispatch, proxyUuid });
    fetchGatewayDetails({ config, dispatch, proxyUuid });
    fetchGateways({ config, dispatch });
    fetchApis({ config, dispatch });
    fetchAccountPlans({ config, dispatch });
    fetchApplicationApiKeysCount({ config, dispatch });
    if (featureFlagApiPlan) {
      fetchApiPlans({ config, dispatch });
    }
    fetchAllOrganizations({ config, dispatch });
    fetchSelectedOrganizations({ config, dispatch, proxyUuid });
  }, []);

  // checkEnrollmentStatus
  useEffect(() => {
    let timeIntervalObj = null;
    if (get(gatewayDetails, 'EnrollmentStatus') === 'ACTIVE') {
      clearTimeout(timeIntervalObj);
    } else if (get(gatewayDetails, 'EnrollmentStatus') === 'PENDING') {
      timeIntervalObj = setTimeout(() => {
        fetchGatewayDetails({ config, dispatch, proxyUuid });
      }, refreshTimeInterval);
    }
  }, [gatewayDetails]);

  // canDeleteProxy
  let canDeleteProxy = true;
  if (get(gatewayDetails, 'EnrollmentStatus') === 'ACTIVE') {
    let activeProxies = 0;
    gateways.map((obj) => {
      if (get(obj, 'EnrollmentStatus') === 'ACTIVE') {
        activeProxies++;
      }
      return true;
    });
    canDeleteProxy = activeProxies > 1;
  }

  let apiKeyDeploymentTypeValue = '';
  if (apiKeyProxyType === 'AUTOMATIC') { apiKeyDeploymentTypeValue = 'Automatic'; }
  if (apiKeyProxyType === 'ON_DEMAND') { apiKeyDeploymentTypeValue = 'On Demand'; }

  let lastSyncErrorMsg = '';
  let apiDeploymentTypeValue = '';
  let ssgProxyUrlValue = '';
  let ssgVersionValue = '';
  let otkVersionValue = '';
  let enrollUrlValue = '';
  let displayProxyEnrollment = false;

  const deploymentType = PROXY_TYPE_ITEMS.filter((obj) => obj.key === get(gatewayDetails, 'DeploymentType'));
  if (deploymentType.length === 1) {
    apiDeploymentTypeValue = deploymentType[0].name;
  }
  switch (get(gatewayDetails, 'EnrollmentStatus')) {
    case 'PENDING': {
      lastSyncErrorMsg = 'Pending Enrollment';
      ssgProxyUrlValue = 'Pending Enrollment';
      ssgVersionValue = 'Pending Enrollment';
      otkVersionValue = 'Pending Enrollment';
      enrollUrlValue = get(proxyDetails, 'enrollmentUrl');
      displayProxyEnrollment = true;
      break;
    }
    default: {
      lastSyncErrorMsg = 'Service Disruption';
      const strLogs = get(gatewayDetails, 'Logs');
      if (strLogs) {
        const logs = JSON.parse(strLogs);
        ssgProxyUrlValue = logs.cluster_name;
        ssgVersionValue = logs.version;
        otkVersionValue = logs.otk_version ? logs.otk_version : 'Unknown version';
      }
    }
  }

  const onEditProxy = () => {
    history.push(`/publish/api-proxy/edit/${proxyUuid}`);
  };

  const openDeleteDialog = () => {
    setShowDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
  };

  const onDeleteProxy = () => {
    deleteProxy({ config, dispatch, proxyUuid });
    closeDeleteDialog();
  };

  const showCopied = () => {
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 5000);
  };

  useEffect(() => {
    if (deleteProxyStatus === 'SUCCESS') {
      window.location.href = '/admin/api-proxy';
    } else if (deleteProxyStatus === 'FAIL') {
      setNotificationMessage('There was an error while deleting proxy.');
      setNotificationStatus('error');
    }
  }, [deleteProxyStatus]);

  const isApiAutoDeploy = get(gatewayDetails, 'DeploymentType') === 'AUTOMATIC';
  const isAppAutoDeploy = apiKeyProxyType === 'AUTOMATIC';
  const selectedOrgsList = selectedOrganizations.filter(o =>
    organizations.find(o2 => o.x === o2.x));
  return (
    <div className={classes.content} id="api-proxy-details-page">
      {notificationMessage &&
        <AlertMessages
          id="api-proxy-details-notifications"
          message={notificationMessage}
          variant={notificationStatus}
          containerClass={classes.notificationWidthClass}
          onClose={() => {
            setNotificationMessage('');
            setNotificationStatus('');
          }}
        />
      }
      {showDeleteDialog &&
        <AlertDialog
          isOpen={showDeleteDialog}
          title={'Delete Proxy'}
          description={'Are you sure you want to delete the proxy? Deletion will permanently remove the proxy from the Portal'}
          submitText={'Ok'}
          cancelText={'Cancel'}
          onClose={() => closeDeleteDialog()}
          onSubmit={() => onDeleteProxy()}
          onCancel={() => closeDeleteDialog()}
          dialogId="api-proxy-delete-dialog"
        />
      }
      <h1 className={classes.pageTitle}>{get(gatewayDetails, 'Name')}</h1>
      <div className={classes.actionButtonsContainer}>
        <Button
          onClick={() => onEditProxy()}
          color="secondary"
          variant="contained"
          data-testid={'edit-proxy'}
        >
          {'Edit'}
        </Button>
        <Button
          onClick={openDeleteDialog}
          color="primary"
          variant="outlined"
          disabled={!canDeleteProxy}
          data-testid={'delete-proxy'}
        >
          {'Delete'}
        </Button>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.bodyContent}>
        {displayProxyEnrollment &&
          <div id="pending-enrollment-section">
            <h2 className={classes.enrollmentSectionTitle}>{'Complete Proxy Enrollment'}</h2>
            <div className={classes.enrollSection}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Paper className={classes.enrollSectionCard}>
                    <span className={classes.enrollSectionColTitle}>{'Enrollment URL'}</span>
                    <br />
                    <input id={'enrollUrl'} type={'text'} readOnly value={enrollUrlValue} />
                    <Button
                      color="secondary"
                      variant="contained"
                      className={classes.selectUrlButton}
                      onClick={() => {
                        showCopied();
                        copy(enrollUrlValue);
                      }}
                    >
                      {'Select URL'}
                    </Button>
                    {copied && <span className={classes.copiedText}>URL selected!!!</span>}
                    <br />
                    <span className={classes.enrollSectionColDesc}>
                      {'Select the following URL then copy & paste it into the Policy Manager. This URL will expire in 24 hours.'}
                    </span>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper className={classes.enrollSectionCard}>
                    <span className={classes.enrollSectionColTitle}>{'Test Connection'}</span>
                    <br />
                    <span className={classes.enrollSectionColDesc}>
                      {'Once URL is pasted into Policy Manager, return here to wait for connection.'}
                    </span>
                    <br />
                    <CircularProgress classes={{ root: classes.loadingSpinner }} />
                  </Paper>
                </Grid>
              </Grid>
            </div>
            <br />
            <Divider className={classes.divider} />
            <br />
          </div>
        }
        <Paper classes={{ root: classes.attributeContainer }}>
          <span>API Deployment Type:</span> {apiDeploymentTypeValue}
          {useNewAutoSync && isApiAutoDeploy &&
            <Tooltip
              data-apim-test="proxy-details-api-helptext"
              title={getI18nFormattedMessage('label.proxy.details.api.title.helptext')}
              arrow placement="right"
            >
              <HelpIcon className={classes.helpIcon} />
            </Tooltip>
          }
        </Paper>
        <Paper classes={{ root: classes.attributeContainer }}>
          <span>Key Deployment Type:</span> {apiKeyDeploymentTypeValue}
        </Paper>
        <Paper classes={{ root: classes.attributeContainer }}>
          <span>API Proxy URL:</span> {ssgProxyUrlValue}
        </Paper>
        <Paper classes={{ root: classes.attributeContainer }}>
          <span>Proxy Version:</span> {ssgVersionValue}
        </Paper>
        <Paper classes={{ root: classes.attributeContainer }}>
          <span>OTK Version:</span> {otkVersionValue}
        </Paper>
        <br />
        {useNewAutoSync && isApiAutoDeploy ?
          <ApiCard
            details={gatewayDetails}
            title={'APIs'}
            titleIcon={<CloudIcon />}
            totalCount={portalApis.totalElements}
            portalApis={portalApis}
            portalPublishedCount={portalPublishedApis.totalElements}
            gatewayPublishedCount={gatewayPublishedApis.totalElements}
            keySyncLog={'ApiSyncLog'}
            keySyncTime={'ApiSyncTime'}
            keySyncDisruption={'ApiSyncDisruption'}
            lastSyncErrorMsg={lastSyncErrorMsg}
            displayLastSync={isApiAutoDeploy}
            displayProxyCount={isApiAutoDeploy}
          />
        :
          <DetailCard
            details={gatewayDetails}
            title={'APIs'}
            titleIcon={<CloudIcon />}
            totalCount={apis.length}
            keySyncLog={'ApiSyncLog'}
            keySyncTime={'ApiSyncTime'}
            keySyncDisruption={'ApiSyncDisruption'}
            lastSyncErrorMsg={lastSyncErrorMsg}
            displayLastSync={isApiAutoDeploy}
            displayProxyCount={isApiAutoDeploy}
            isApiCard
          />
        }
        <DetailCard
          details={gatewayDetails}
          title={'Application API Keys'}
          titleIcon={<LayersIcon />}
          totalCount={applicationApiKeysCount}
          keySyncLog={'AppSyncLog'}
          keySyncTime={'AppSyncTime'}
          keySyncDisruption={'AppSyncDisruption'}
          lastSyncErrorMsg={lastSyncErrorMsg}
          displayLastSync={isAppAutoDeploy}
          displayProxyCount={isAppAutoDeploy}
        />
        <DetailCard
          details={gatewayDetails}
          title={'Account Plans'}
          titleIcon={<InsertDriveFileIcon />}
          totalCount={accountPlans.length}
          keySyncLog={'AccountPlanSyncLog'}
          keySyncTime={'AccountPlanSyncTime'}
          keySyncDisruption={'AccountPlanSyncDisruption'}
          lastSyncErrorMsg={lastSyncErrorMsg}
        />
        <DetailCard
          details={gatewayDetails}
          title={'API Plans'}
          titleIcon={<InsertDriveFileIcon />}
          totalCount={apiPlansCount}
          keySyncLog={'ApiPlanSyncLog'}
          keySyncTime={'ApiPlanSyncTime'}
          keySyncDisruption={'ApiPlanSyncDisruption'}
          lastSyncErrorMsg={lastSyncErrorMsg}
          featureFlag={featureFlagApiPlan}
          emptyResultsText={'API Plans are currently disabled.'}
        />
        <DetailCard
          details={gatewayDetails}
          title={'Organizations'}
          titleIcon={<GroupIcon />}
          displayPortalCount={false}
          displayProxyCount={false}
          selectedList={selectedOrgsList.map(obj => ({ key: obj.uuid, value: obj.name }))}
          displaySelectedList
          displayLastSync={false}
        />
      </div>
      <Footer />
    </div>
  );
};
const mapStateToProps = state => ({
  config: getConfig(state),
  featureFlags: getFeatureFlags(state),
  proxyDetails: getProxyDetails(state),
  apiKeyProxyType: getApiKeyProxyType(state),
  gateways: getGateways(state),
  gatewayDetails: getGatewayDetails(state),
  apis: getApis(state),
  applicationApiKeysCount: getApplicationApiKeysCount(state),
  accountPlans: getAccountPlans(state),
  apiPlansCount: getApiPlansCount(state),
  organizations: getOrganizations(state),
  selectedOrganizations: getSelectedOrganizations(state),
  deleteProxyStatus: getDeleteProxyStatus(state),
  useNewAutoSync: getApiSyncFeatureFlag(state),
  portalApis: getPortalApis(state),
  portalPublishedApis: getPortalPublishedApis(state),
  gatewayPublishedApis: getGatewayPublishedApis(state),
});

const mapDispatchToProps = {
  fetchProxyDetails,
  fetchGateways,
  fetchGatewayDetails,
  fetchApis,
  fetchApplicationApiKeysCount,
  fetchAccountPlans,
  fetchApiPlans,
  fetchAllOrganizations,
  fetchSelectedOrganizations,
  fetchApiSyncFeatureFlag,
  deleteProxy,
};

ApiProxyDetails.propTypes = {
  proxyDetails: PropTypes.shape({}),
  apiKeyProxyType: PropTypes.string,
  gatewayDetails: PropTypes.shape({}),
  gateways: PropTypes.arrayOf(PropTypes.shape({})),
  apis: PropTypes.arrayOf(PropTypes.shape({})),
  applicationApiKeysCount: PropTypes.number,
  accountPlans: PropTypes.arrayOf(PropTypes.shape({})),
  apiPlansCount: PropTypes.number,
  organizations: PropTypes.arrayOf(PropTypes.shape({})),
  selectedOrganizations: PropTypes.arrayOf(PropTypes.shape({})),
  deleteProxyStatus: PropTypes.string,
  featureFlags: PropTypes.shape({}),
  useNewAutoSync: PropTypes.bool,
  portalApis: PropTypes.shape({}),
  gatewayPublishedApis: PropTypes.shape({}),
  portalPublishedApis: PropTypes.shape({}),
  config: PropTypes.shape({}),
  history: PropTypes.shape({}),
  classes: PropTypes.shape({}),
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ApiProxyDetails);



// WEBPACK FOOTER //
// ./src/pages/ApiProxy/Details/index.js